import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  AccessTime as TimeIcon,
  Share as ShareIcon,
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
} from '@mui/icons-material';
import { getCategoryColor } from '../../theme/theme';
import ReactionBar from './ReactionBar';
import CommentsSection from './CommentsSection';
import ShareDialog from './ShareDialog';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Get initials from username
const getInitials = (name) => {
  if (!name || name === 'Anonymous') return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

function PrayerDetailDialog({
  open,
  onClose,
  prayer,
  currentUserId,
  comments = [],
  commentsLoading = false,
  collected = false,
  onCollect,
  onReact,
  onAddComment,
  onDeleteComment,
  onReportComment,
  onLoadComments,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Load comments when dialog opens
  useEffect(() => {
    if (open && prayer && onLoadComments) {
      onLoadComments(prayer.id);
    }
  }, [open, prayer?.id]);

  if (!prayer) return null;

  const {
    content,
    category = 'General',
    created_at,
    is_anonymous,
    username,
    reactions = {},
    user_reaction = null,
    circle_name,
  } = prayer;

  const displayName = is_anonymous ? 'Anonymous' : (username || 'Unknown');
  const categoryColor = getCategoryColor(category);

  const handleReact = (reactionType) => {
    if (onReact) onReact(prayer, reactionType);
  };

  const handleCollect = () => {
    if (onCollect) onCollect(prayer);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            maxHeight: fullScreen ? '100%' : '90vh',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: is_anonymous ? 'grey.400' : 'primary.main',
              }}
            >
              {is_anonymous ? <PersonIcon /> : getInitials(displayName)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {displayName}
                {is_anonymous && (
                  <LockIcon
                    sx={{
                      fontSize: 14,
                      ml: 0.5,
                      color: 'text.disabled',
                      verticalAlign: 'middle',
                    }}
                  />
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <TimeIcon sx={{ fontSize: 12 }} />
                {formatRelativeTime(created_at)}
                {circle_name && (
                  <>
                    <span>•</span>
                    {circle_name}
                  </>
                )}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={category}
              size="small"
              sx={{
                bgcolor: `${categoryColor}20`,
                color: categoryColor,
                fontWeight: 500,
                height: 24,
              }}
            />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Prayer content */}
          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'text.primary',
                whiteSpace: 'pre-wrap',
              }}
            >
              {content}
            </Typography>
          </Box>

          {/* Category indicator */}
          <Box
            sx={{
              height: 4,
              bgcolor: categoryColor,
              opacity: 0.6,
            }}
          />

          {/* Actions section */}
          <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            {/* Reactions */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <ReactionBar
                reactions={reactions}
                userReaction={user_reaction}
                onReact={handleReact}
              />

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={handleCollect}
                  sx={{
                    color: collected ? 'error.main' : 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'rgba(229, 62, 62, 0.1)',
                    },
                  }}
                >
                  {collected ? <FavoriteFilledIcon /> : <FavoriteIcon />}
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => setShareDialogOpen(true)}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'secondary.main',
                      bgcolor: 'rgba(212, 165, 116, 0.1)',
                    },
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Comments section */}
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Comments
            </Typography>

            <CommentsSection
              prayerId={prayer.id}
              comments={comments}
              currentUserId={currentUserId}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              onReportComment={onReportComment}
              loading={commentsLoading}
              maxVisible={5}
              placeholder="Share your thoughts or offer support..."
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        prayer={prayer}
      />
    </>
  );
}

export default PrayerDetailDialog;
